import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Save, Info, ChevronDown } from "lucide-react";

export default function AdminShipping() {
  const [catalog, setCatalog] = useState([]);
  const [enabled, setEnabled] = useState([]);
  const [disabledSvcs, setDisabledSvcs] = useState([]);
  const [expanded, setExpanded] = useState({});
  const [biteshipSet, setBiteshipSet] = useState(false);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    api.get("/shipping/couriers/catalog").then((r) => setCatalog(r.data.catalog)).catch(() => {});
    api.get("/admin/settings").then((r) => {
      setEnabled(r.data.couriers_enabled || ["jne", "spx"]);
      setDisabledSvcs(r.data.courier_services_disabled || []);
      setBiteshipSet(r.data.biteship_key_set);
    }).catch(() => {});
  }, []);

  const toggleCourier = (code) => setEnabled((e) => (e.includes(code) ? e.filter((c) => c !== code) : [...e, code]));
  const toggleSvc = (courier, svc) => {
    const key = `${courier}:${svc}`;
    setDisabledSvcs((d) => (d.includes(key) ? d.filter((k) => k !== key) : [...d, key]));
  };

  const save = async () => {
    if (!enabled.length) return toast.error("Minimal 1 ekspedisi harus aktif");
    setBusy(true);
    try {
      await api.put("/admin/settings", { couriers_enabled: enabled, courier_services_disabled: disabledSvcs });
      toast.success("Pengaturan pengiriman disimpan — langsung berlaku di checkout pelanggan");
    } catch {
      toast.error("Gagal menyimpan pengaturan");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="space-y-4 max-w-3xl">
      <div>
        <h1 className="font-display text-2xl font-bold" data-testid="shipping-settings-title">Pengaturan Pengiriman</h1>
        <p className="text-sm text-slate-500">Aktifkan ekspedisi dan jenis layanan yang bisa dipilih pelanggan saat checkout. Daftar mengikuti kurir yang didukung Biteship.</p>
      </div>

      <div className={`rounded-xl border px-4 py-3 text-xs flex gap-2 ${biteshipSet ? "bg-emerald-50 border-emerald-200 text-emerald-700" : "bg-amber-50 border-amber-200 text-amber-700"}`} data-testid="biteship-status">
        <Info className="w-4 h-4 flex-shrink-0 mt-0.5" />
        {biteshipSet
          ? "API key Biteship aktif — ongkir & tracking checkout memakai data live sesuai ekspedisi & layanan yang aktif."
          : "API key Biteship belum diisi (menu Pengaturan) — ongkir checkout masih mode simulasi; ekspedisi & layanan yang aktif tetap tampil dengan tarif simulasi."}
      </div>

      <div className="bg-white rounded-2xl border border-slate-200 divide-y divide-slate-100" data-testid="courier-list">
        {catalog.map((c) => {
          const on = enabled.includes(c.code);
          const svcs = c.services || [];
          const activeSvcs = svcs.filter((s) => !disabledSvcs.includes(`${c.code}:${s.code}`));
          const isOpen = !!expanded[c.code];
          return (
            <div key={c.code} data-testid={`courier-row-${c.code}`}>
              <div className="flex items-center gap-3 px-4 py-3.5">
                <input type="checkbox" data-testid={`courier-toggle-${c.code}`} checked={on} onChange={() => toggleCourier(c.code)} className="w-4 h-4 accent-violet-600 cursor-pointer" />
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-semibold text-slate-800">{c.name}</div>
                  <div className="text-[11px] text-slate-400">Kode: {c.code} • Layanan aktif: {on ? `${activeSvcs.length}/${svcs.length}` : "-"}</div>
                </div>
                <span className={`text-[10px] px-2 py-0.5 rounded-full font-semibold ${on ? "bg-emerald-100 text-emerald-700" : "bg-slate-100 text-slate-400"}`}>
                  {on ? "Aktif di checkout" : "Nonaktif"}
                </span>
                {on && svcs.length > 0 && (
                  <button data-testid={`courier-expand-${c.code}`} onClick={() => setExpanded((x) => ({ ...x, [c.code]: !isOpen }))}
                    className="text-xs text-violet-700 font-semibold flex items-center gap-1 hover:underline">
                    Layanan <ChevronDown className={`w-3.5 h-3.5 transition-transform ${isOpen ? "rotate-180" : ""}`} />
                  </button>
                )}
              </div>
              {on && isOpen && (
                <div className="bg-slate-50 border-t border-slate-100 px-4 py-2 divide-y divide-slate-100" data-testid={`courier-services-${c.code}`}>
                  {svcs.map((s) => {
                    const svcOn = !disabledSvcs.includes(`${c.code}:${s.code}`);
                    return (
                      <label key={s.code} className="flex items-center gap-3 py-2 cursor-pointer">
                        <input type="checkbox" data-testid={`service-toggle-${c.code}-${s.code}`} checked={svcOn} onChange={() => toggleSvc(c.code, s.code)} className="w-3.5 h-3.5 accent-violet-600" />
                        <span className="text-xs text-slate-700 flex-1">{s.name}</span>
                        <span className="text-[10px] font-mono text-slate-400">{s.code}</span>
                        <span className={`text-[10px] px-1.5 py-0.5 rounded-full font-semibold ${svcOn ? "bg-emerald-100 text-emerald-700" : "bg-red-50 text-red-500"}`}>{svcOn ? "Aktif" : "Nonaktif"}</span>
                      </label>
                    );
                  })}
                </div>
              )}
            </div>
          );
        })}
      </div>

      <button data-testid="shipping-save-btn" onClick={save} disabled={busy}
        className="h-10 px-6 rounded-xl bg-violet-600 hover:bg-violet-700 text-white text-sm font-semibold flex items-center gap-2 disabled:opacity-40 transition-colors">
        <Save className="w-4 h-4" /> {busy ? "Menyimpan..." : "Simpan Pengaturan"}
      </button>
    </div>
  );
}
